// Shared sorting kit for the admin data grids (sc-100769).
//
// Eleven `useReactTable` pages across four admin apps duplicate the same table block, so the pieces a
// sortable grid needs that are NOT page-specific live here: the table options that pin TanStack's
// data-dependent defaults, the accessors that map "empty" to `undefined` so `sortUndefined: "last"`
// applies, and the base-order normalizer that makes the tie-break a domain key.
//
// Pure functions only — no React, no table-core import — so every helper is unit-testable on its own.

/**
 * Spread into every sortable admin grid's `useReactTable` options.
 *
 * Both entries pin a table-core default that is otherwise wrong for these grids (verified against
 * `@tanstack/table-core@8.21.3`):
 * - `enableSortingRemoval` defaults to **true**, so a third header click would drop the grid back into
 *   storage order — the exact unordered state these grids exist to escape.
 * - `sortDescFirst` is otherwise derived by `getAutoSortDir()`, which reads row 0 of the data (string →
 *   asc, anything else → desc), so first-click direction would vary with the rows on screen.
 * - `enableMultiSort` resolves to `!!column.accessorFn` when unset, so every sortable column would opt
 *   in and a **shift-click** on a second header would ADD a sort rather than replace it
 *   (`isMultiSortEvent` is `e => e.shiftKey`, and the header forwards the event through
 *   `getToggleSortingHandler()`). Multi-column sort is explicitly out of scope for these grids, and two
 *   simultaneously-sorted columns would also put `aria-sort` on two headers.
 */
export const SORTABLE_GRID_OPTIONS = {
  enableSortingRemoval: false,
  sortDescFirst: false,
  enableMultiSort: false,
} as const;

/**
 * Text accessor: `null` / `undefined` / `""` all become `undefined` so a column's
 * `sortUndefined: "last"` applies to them. Real text passes through unchanged.
 */
export function sortableText(value: string | null | undefined): string | undefined {
  return value ? value : undefined;
}

/**
 * Instant accessor: an ISO timestamp becomes epoch milliseconds; `null` / `undefined` / an unparseable
 * string become `undefined`. Dates therefore compare as instants, never as localized display strings.
 */
export function sortableInstant(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

/**
 * Numeric accessor: `null` / `undefined` become `undefined`. `0` is a value and is preserved — a
 * zero-millisecond duration sorts among the numbers, not with the empty cells.
 */
export function sortableNumber(value: number | null | undefined): number | undefined {
  return value == null ? undefined : value;
}

/**
 * Returns a NEW array in ascending unique-key order, leaving the input untouched.
 *
 * This is the table's base order, which buys both halves of the stability contract at once:
 * TanStack's tie-break for equal values is `rowA.index - rowB.index` (the base-array order), and two
 * rows whose sort values are BOTH `undefined` short-circuit inside `getSortedRowModel` before that
 * tie-break is ever reached — so their relative order is the base order too. Normalizing to the domain
 * key means a poll that returns the same rows in a different storage order cannot reshuffle the grid.
 */
export function orderByKey<T>(rows: T[], getKey: (row: T) => string): T[] {
  return [...rows].sort((a, b) => {
    const ka = getKey(a);
    const kb = getKey(b);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });
}
